// client/src/components/pages/WeighbridgeCompliance/OperatorModal.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-5: the weighbridges ticked here were sent as `assignedAssets`, a key the
// server schema strips, so no assignment was ever saved, and the modal read
// `operator.assignedAssets`, which no endpoint returns. It now sends
// `assigned_assets: [{ asset_id, assigned_date }]` (the operator's whole active
// set) and pre-fills from the list row's `assigned_assets`.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import type { AxiosResponse } from 'axios';
import { toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import type {
  ComplianceAssetRow,
  ComplianceRequestError,
  OperatorPayload,
  OperatorRow,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

export const QUALIFICATION_OPTIONS = [
  { value: 'SCWO', label: 'SCWO - SASCO Certified Weighbridge Operator' },
  { value: 'NRCS_VERIFICATION', label: 'NRCS Verification Officer' },
  { value: 'NRCS_REPAIRER', label: 'NRCS Repairer' },
  { value: 'INTERNAL', label: 'Internal Competency' },
  { value: 'OTHER', label: 'Other' },
];

/** The label of a stored qualification code (an unknown value is shown as stored). */
export const qualificationLabel = (code: string | null | undefined): string =>
  QUALIFICATION_OPTIONS.find((o) => o.value === code)?.label || code || '';

interface AssignedAssetEntry {
  assetId: number;
  assetName: string;
  assignmentDate: Date;
}

interface OperatorForm {
  fullName: string;
  employeeNumber: string;
  qualification: string;
  certificateNumber: string;
  issuingBody: string;
  certificationDate: Date | null;
  expiryDate: Date | null;
  status: string;
  assignedAssets: AssignedAssetEntry[];
}

type OperatorFormErrors = Partial<Record<keyof OperatorForm, string>>;

const initialForm: OperatorForm = {
  fullName: '',
  employeeNumber: '',
  qualification: '',
  certificateNumber: '',
  issuingBody: '',
  certificationDate: null,
  expiryDate: null,
  status: 'Active',
  assignedAssets: [],
};

const parseDate = (value: string | null | undefined): Date | null =>
  value ? parseISO(value) : null;

/** The message of a failed save: the server's `error` plus its `details` (it never sends `message`). */
const saveErrorMessage = (error: unknown): string => {
  const data = (error as ComplianceRequestError)?.response?.data;
  if (!data?.error) return 'Failed to save operator';
  return data.details && data.details.length > 0
    ? `${data.error}: ${data.details.join('; ')}`
    : data.error;
};

export interface OperatorModalProps {
  operator: OperatorRow | null;
  onClose: () => void;
  onSave: () => void;
}

export const OperatorModal = ({ operator, onClose, onSave }: OperatorModalProps) => {
  const [form, setForm] = useState<OperatorForm>(initialForm);
  const [errors, setErrors] = useState<OperatorFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<ComplianceAssetRow[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(true);

  const isEdit = Boolean(operator);

  useEffect(() => {
    if (operator) {
      setForm({
        fullName: operator.full_name || '',
        employeeNumber: operator.employee_number || '',
        qualification: operator.qualification || '',
        certificateNumber: operator.certificate_number || '',
        issuingBody: operator.issuing_body || '',
        certificationDate: parseDate(operator.certification_date),
        expiryDate: parseDate(operator.expiry_date),
        status: operator.status || 'Active',
        assignedAssets: (operator.assigned_assets || []).map((a) => ({
          assetId: a.asset_id,
          assetName: a.asset_name || '',
          assignmentDate: parseDate(a.assigned_date) || new Date(),
        })),
      });
    } else {
      setForm(initialForm);
    }
    setErrors({});
  }, [operator]);

  const fetchAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const response = (await api.assets.getAll({ status: 'Active', limit: 100 })) as AxiosResponse<
        PaginatedBody<ComplianceAssetRow> | ComplianceAssetRow[]
      >;
      const d = response.data;
      setAvailableAssets(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load weighbridge assets');
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const validate = () => {
    const newErrors: OperatorFormErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.employeeNumber.trim()) newErrors.employeeNumber = 'Employee number is required';
    if (!form.qualification) newErrors.qualification = 'Qualification is required';
    if (!form.certificationDate) newErrors.certificationDate = 'Certification date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAssetToggle = (asset: ComplianceAssetRow) => {
    setForm((prev) => {
      const existing = prev.assignedAssets.find((a) => a.assetId === asset.id);
      if (existing) {
        return {
          ...prev,
          assignedAssets: prev.assignedAssets.filter((a) => a.assetId !== asset.id),
        };
      }
      return {
        ...prev,
        assignedAssets: [
          ...prev.assignedAssets,
          {
            assetId: asset.id,
            assetName: asset.name || '',
            assignmentDate: new Date(),
          },
        ],
      };
    });
  };

  const handleAssignmentDateChange = (assetId: number, date: Date | null) => {
    setForm((prev) => ({
      ...prev,
      assignedAssets: prev.assignedAssets.map((a) =>
        a.assetId === assetId ? { ...a, assignmentDate: date || new Date() } : a
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload: OperatorPayload = {
        full_name: form.fullName,
        employee_number: form.employeeNumber,
        qualification: form.qualification,
        certificate_number: form.certificateNumber,
        issuing_body: form.issuingBody,
        certification_date: form.certificationDate ? toApiDateString(form.certificationDate) : null,
        expiry_date: form.expiryDate ? toApiDateString(form.expiryDate) : null,
        status: form.status,
        assigned_assets: form.assignedAssets.map((a) => ({
          asset_id: a.assetId,
          assigned_date: toApiDateString(a.assignmentDate || new Date()),
        })),
      };
      if (isEdit && operator) {
        await api.operators.update(operator.id, { ...payload });
        toast.success('Operator updated successfully');
      } else {
        await api.operators.create({ ...payload });
        toast.success('Operator created successfully');
      }
      onSave();
    } catch (error) {
      toast.error(saveErrorMessage(error));
      console.error('Error saving operator:', error);
    } finally {
      setSaving(false);
    }
  };

  const isAssetAssigned = (assetId: number) =>
    form.assignedAssets.some((a) => a.assetId === assetId);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {isEdit ? 'Edit Operator' : 'Add Operator'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Full Name *
              </label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.fullName ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="e.g. John Mokoena"
              />
              {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Employee Number *
              </label>
              <input
                type="text"
                value={form.employeeNumber}
                onChange={(e) => setForm({ ...form, employeeNumber: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.employeeNumber ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="e.g. EMP-0042"
              />
              {errors.employeeNumber && (
                <p className="text-xs text-red-500 mt-1">{errors.employeeNumber}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Qualification *
            </label>
            <ThemedSelect
              value={form.qualification}
              onChange={(e) => setForm({ ...form, qualification: String(e.target.value) })}
              className={`w-full px-3 py-2.5 border ${errors.qualification ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
            >
              <option value="">Select qualification...</option>
              {QUALIFICATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </ThemedSelect>
            {errors.qualification && (
              <p className="text-xs text-red-500 mt-1">{errors.qualification}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Certificate Number
              </label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => setForm({ ...form, certificateNumber: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="e.g. CERT-2024-001"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Issuing Body
              </label>
              <input
                type="text"
                value={form.issuingBody}
                onChange={(e) => setForm({ ...form, issuingBody: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="e.g. SASCO, NRCS"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Certification Date *
              </label>
              <DatePicker
                selected={form.certificationDate}
                onChange={(date: Date | null) => setForm({ ...form, certificationDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className={`w-full px-3 py-2.5 border ${errors.certificationDate ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              />
              {errors.certificationDate && (
                <p className="text-xs text-red-500 mt-1">{errors.certificationDate}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Expiry Date
              </label>
              <DatePicker
                selected={form.expiryDate}
                onChange={(date: Date | null) => setForm({ ...form, expiryDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Status
            </label>
            <ThemedSelect
              value={form.status}
              onChange={(e) => setForm({ ...form, status: String(e.target.value) })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </ThemedSelect>
          </div>

          <div className="pt-2">
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Assigned Weighbridges
            </label>
            {loadingAssets ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : availableAssets.length === 0 ? (
              <p className="text-sm text-slate-400 py-3">No weighbridge assets available</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableAssets.map((asset) => {
                  const assigned = isAssetAssigned(asset.id);
                  const assignedEntry = form.assignedAssets.find((a) => a.assetId === asset.id);
                  return (
                    <div
                      key={asset.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                        assigned
                          ? 'border-teal-300 bg-teal-50/50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => handleAssetToggle(asset)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center ${
                            assigned ? 'bg-teal-500' : 'border-2 border-slate-300'
                          }`}
                        >
                          {assigned && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{asset.name}</span>
                      </div>
                      {assigned && (
                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[11px] text-slate-400">Assigned:</span>
                          <DatePicker
                            selected={assignedEntry?.assignmentDate || new Date()}
                            onChange={(date: Date | null) =>
                              handleAssignmentDateChange(asset.id, date)
                            }
                            dateFormat="yyyy-MM-dd"
                            className="w-28 px-2 py-1 border border-slate-200 rounded text-xs focus:ring-1 focus:ring-teal-500"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Operator' : 'Add Operator'}
          </button>
        </div>
      </div>
    </div>
  );
};
